<template>
  <div
    class="flex flex-grow transition-all duration-300"
    :style="{ padding: '40px 0px 40px 0px' }"
  >
    <div
      class="flex-grow flex-shrink transition-all duration-300"
      @click="$emit('change-focus-id', 0)"
    ></div>
    <div
      class="flex-shrink-0 worksheet-container bg-pure-white"
      :style="{ width: '800px', padding: '40px 0px 40px 0px' }"
    >
      <div
        v-if="addGraphLoading"
        class="fixed top-0 left-0 z-20 flex items-center justify-center flex-grow w-full h-full opacity-50 bg-pure-white"
      >
        <div class="blum-loader"></div>
      </div>
      <div
        v-if="report.include_header"
        id="worksheet-header"
        class="flex items-center justify-between"
        :style="{ padding: '12px 40px', gap: '16px' }"
      >
        <div v-if="report.center_logo_url" class="flex flex-shrink-0">
          <img
            :src="report.center_logo_url"
            alt="logo"
            :style="{ height: '40px' }"
          />
        </div>
        <div class="w-full text-right truncate">
          <div class="truncate">
            <BlTextBody3
              :text="report.client_name"
              class="truncate text-slate-700"
            />
          </div>
          <div>
            <BlTextBody3 text="Report for " class="text-slate-700" />
            <BlTextBody3
              :text="displayDate({ date: report.start_date })"
              class="text-slate-700"
            />
            <BlTextBody3 text=" - " class="text-slate-700" />
            <BlTextBody3
              :text="displayDate({ date: report.end_date })"
              class="text-slate-700"
            />
          </div>
        </div>
      </div>
      <div
        id="worksheet-title"
        class="flex items-center justify-center"
        :style="{ padding: '12px 40px' }"
      >
        <div class="font-bold text-slate-1000" :style="{ fontSize: '26px' }">
          {{ report.name }}
        </div>
      </div>
      <draggable
        handle=".handle"
        @update="onMoveSection($event)"
        @start="onStartDrag($event)"
        @end="onEndDrag"
      >
        <transition-group>
          <div
            v-for="section in sections"
            :key="section.id"
            :id="`report-section-${section.id}`"
            :data-id="section.id"
          >
            <div
              v-if="mode === 'edit' || dragMove"
              class="flex items-center justify-center rounded"
              :class="[
                addTextAreaLoading ? 'cursor-wait' : 'cursor-pointer',
                dragMove
                  ? 'hover:bg-light-purple-100'
                  : 'opacity-0 hover:opacity-100'
              ]"
              :style="{ padding: '0px 4px', gap: '8px' }"
              @click="onAddTextArea(section.position)"
              @mouseup="onDropGraph(section.position)"
            >
              <div class="h-0.5 w-full bg-slate-500 rounded"></div>
              <BlIcon icon="plus" color="slate-8" class="w-6 h-6" />
              <BlTextBody2
                v-if="dragMove"
                text="Drop here to add the graph"
                class="flex-shrink-0"
              />
              <BlTextBody2 v-else text="Add text block" class="flex-shrink-0" />
              <div class="h-0.5 w-full bg-slate-500 rounded"></div>
            </div>
            <SectionItem
              :data-id="section.id"
              :section="section"
              :mode="dragMove ? 'dragging' : mode"
              :isFocused="sectionFocusedId === section.id"
              :isDragging="isDragging"
              :isDraggingGraph="isDraggingGraph"
              @change-saving="$emit('change-saving', $event)"
              @change-focus-id="$emit('change-focus-id', $event)"
              @drop-graph="onDropGraph(section.position)"
            />
          </div>
        </transition-group>
      </draggable>
      <div
        v-if="mode === 'edit' || dragMove"
        :style="{ padding: '4px 40px 4px 40px', gap: '8px' }"
        @mouseup="onDropGraph(0)"
      >
        <div
          class="flex items-center rounded hover:bg-white"
          :class="[
            addTextAreaLoading ? 'cursor-wait' : 'cursor-pointer',
            dragMove
              ? 'h-32 justify-center border-2 border-light-purple border-dashed'
              : ''
          ]"
          :style="{ padding: '1px 1px', gap: '2px' }"
          @click="onAddTextArea"
        >
          <BlIcon icon="plus" color="slate-8" />
          <BlTextBody2
            v-if="dragMove"
            text="Drop here to add the graph at the end of the report."
          />
          <BlTextBody2 v-else text="Add text block" />
        </div>
      </div>
      <div
        v-if="mode === 'edit'"
        class="w-full h-32"
        @click="$emit('change-focus-id', 0)"
      ></div>
    </div>
    <div
      class="flex-grow flex-shrink transition-all duration-300"
      @click="$emit('change-focus-id', 0)"
    ></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import draggable from "vuedraggable";
import { displayDate } from "@/plugins/helper.js";
import {
  PROMPT_COLORS,
  PROMPT_POINTS,
  PHASE_LINE_COLORS
} from "@/plugins/assets.js";

import SectionItem from "./editor/SectionItem.vue";

import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "ReportEditor",
  components: {
    draggable,
    SectionItem,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlIcon
  },
  props: {
    mode: { type: String }, // edit | preview
    sectionFocusedId: { type: Number },
    dragMove: { type: Boolean },
    dragTarget: { type: Object, default: null }
    // @change-saving
    // @change-focus-id
  },
  data() {
    return {
      isDragging: false,
      isDraggingGraph: false,
      //
      addTextAreaLoading: false,
      //
      addGraphLoading: false
    };
  },
  computed: {
    ...mapGetters(["currentCenter", "targets", "report", "sections"])
  },
  watch: {
    // targets: {
    //   deep: true,
    //   handler(val) {
    //     this.generateGraphOptions(val);
    //   }
    // }
  },
  methods: {
    ...mapActions([
      "getTarget",
      "getSections",
      "getGraphDataSection",
      "updateSection",
      "createSection"
    ]),
    displayDate,
    onFocusSection(section) {
      if (!section) return;
      if (!section.kind) return;
      const arr = document.getElementsByClassName("blum-report-editor");
      for (let idx = 0; idx < arr.length; idx++) {
        arr[idx].classList.remove("editor-focused");
      }
      if (section.kind.includes("graph")) return;
      if (section.kind === "text") {
        const elementId = `section-editor-${section.id}`;
        const el = document.getElementById(elementId);
        if (el) {
          el.classList.add("editor-focused");
        }
      }
    },
    //
    onStartDrag(e) {
      this.isDragging = true;

      const id = e.item.dataset.id;
      const section = this.sections.find(i => i.id === Number(id));
      if (!section) return;

      if (section.kind.includes("graph")) {
        this.isDraggingGraph = true;
      }
    },
    onEndDrag() {
      this.isDragging = false;
      this.isDraggingGraph = false;
    },
    async onMoveSection(e) {
      const id = e.item.dataset.id;
      const position = e.newIndex + 1;

      const section = this.sections.find(i => i.id === Number(id));
      if (!section) return;
      const data = {
        id: section.id,
        section: { position }
      };

      this.$emit("change-saving", true);
      const { success } = await this.updateSection(data);
      if (!success) {
        this.$emit("change-saving", false);
        return;
      }
      // await this.getSections({ slug: this.report.slug });
      this.$emit("change-saving", false);
    },
    async onAddTextArea(position = 0) {
      if (this.addTextAreaLoading) return;
      const data = {
        slug: this.report.slug,
        section: { data: "", kind: "text" }
      };

      if (position) {
        data.section["position"] = position;
      }

      this.addTextAreaLoading = true;
      await this.createSection(data);
      if (position) {
        await this.getSections({ slug: this.report.slug });
      }
      this.addTextAreaLoading = false;
    },
    async onDropGraph(pos) {
      const target = this.dragTarget;
      const position = pos || null;
      if (!target) return;
      if (this.addGraphLoading) return;

      this.addGraphLoading = true;
      this.$emit("change-saving", true);
      let kind = "graph";
      /**
       kind
       * graph -> frequency / other
       * graph_percentage -> percentage
       * graph_pir -> pir
       * graph_duration -> duration
       * graph_prompting -> prompting
       * cumulative_graph_line
       * cumulative_graph_stacked
      */
      if (target.type === "Target::Percentage") {
        kind = "graph_percentage";
      }
      if (target.type === "Target::Pir") {
        kind = "graph_pir";
      }
      if (target.type === "Target::Duration") {
        kind = "graph_duration";
      }
      if (target.type === "Target::Prompting") {
        kind = "graph_prompting";
      }
      if (target.type === "cumulative") {
        kind = "cumulative_graph_line";
      }

      const {
        graphData,
        prompts,
        targetTasks,
        targetProblemBehaviors
      } = await this.fetchGraphDataSection(target);
      const generated = this.generateCollection({
        target,
        graphData,
        prompts,
        targetTasks,
        targetProblemBehaviors
      });
      const stringify = JSON.stringify(generated);

      let htmlText = "";
      if (target.type === "cumulative") {
        htmlText = `<p class="ql-align-center"><strong>${target.name}</strong></p>`;
      } else {
        const statusOptions = {
          mastered: "Mastered",
          in_progress: "In acquisition",
          paused: "Paused",
          discontinued: "Discontinued",
          pending: "Pending"
        };
        const targetStatus = statusOptions[target.status];
        htmlText = `<p class="ql-align-center"><strong>${target.name}</strong></p><p class="ql-align-center">Current status: ${targetStatus}</p><p class="ql-align-center">Curriculum: ${target.curriculum_name}</p>`;
      }

      const textParams = {
        slug: this.report.slug,
        section: {
          data: htmlText,
          kind: "text",
          position
        }
      };
      const { success: textSuccess } = await this.createSection(textParams);

      const graphParams = {
        slug: this.report.slug,
        section: {
          data: stringify,
          kind,
          position: position ? position + 1 : null
        }
      };
      const { success: graphSuccess, data } = await this.createSection(
        graphParams
      );

      await this.getSections({ slug: this.report.slug });

      this.addGraphLoading = false;
      this.$emit("change-saving", false);
      if (textSuccess && graphSuccess) {
        setTimeout(() => {
          this.$emit("change-focus-id", data.id);
        }, 300);
      }
    },
    async fetchGraphDataSection(target) {
      const id =
        target.type === "cumulative" ? this.report.client_id : target.id;
      const type = target.type === "cumulative" ? "cumulative" : "target";

      let prompts = [];
      let targetTasks = [];
      let targetProblemBehaviors = [];
      if (target.type === "Target::Prompting") {
        const { data } = await this.getTarget({ id });
        prompts = data.prompts;
      }
      if (target.type === "Target::Sbt") {
        const { data } = await this.getTarget({ id });
        targetTasks = data.target_tasks;
        targetProblemBehaviors = data.target_problem_behaviors;
      }

      const start = this.displayDate({
        date: this.report.start_date,
        format: "YYYY-MM-DD"
      });
      const end = this.displayDate({
        date: this.report.end_date,
        format: "YYYY-MM-DD"
      });
      let params = `?start_date=${start}&end_date=${end}`;
      if (target.type === "cumulative") {
        const graphType = "line";
        params += `&view=graph&group_by=month`;
        params += `&graph_type=${graphType}`;
      }
      if (target.type === "Target::Prompting") {
        params += `&progress_by=frequency`;
      }

      let graphData = { labels: [], datasets: [], recorded_by: [] };

      const res = await this.getGraphDataSection({ id, type, params });
      if (res) graphData = res;

      return { graphData, prompts, targetTasks, targetProblemBehaviors };
    },
    generateCollection({
      target,
      graphData,
      prompts,
      targetTasks,
      targetProblemBehaviors
    }) {
      const {
        labels = [],
        datasets = [],
        recorded_by: recordedBy = []
      } = graphData;
      const isCumulative = target.type === "cumulative";
      const isStacked = false;
      const isPrompting = target.type === "Target::Prompting";
      const isSbt = target.type === "Target::Sbt";
      const progressBy = isPrompting ? "frequency" : "";

      // setup title
      let newTitle = "";
      if (target.name) {
        newTitle = target.name;
      }
      if (isCumulative) {
        newTitle = "Cumulative graph";
      }

      // setup goal number
      let newGoal = null;
      if (target.goal) {
        newGoal = target.goal;
        if (
          progressBy === "percentage" ||
          (progressBy === "frequency" && target.prompting_format === "custom")
        ) {
          newGoal = null;
        }
      }

      // setup prompts
      let newPrompts = [];
      let newTasks = [];
      let newProblemBehaviors = [];
      if (isPrompting) {
        newPrompts = prompts.map(i => {
          return {
            id: i.id,
            name: i.name,
            abbreviation: i.abbreviation,
            color: i.color,
            shape: i.shape,
            position: i.position,
            is_show: true
          };
        });
      }
      if (isSbt) {
        newTasks = targetTasks.map(i => {
          return {
            id: i.id,
            code: i.code,
            code_definition: i.code_definition,
            color: i.color,
            is_show: true
          };
        });
        newProblemBehaviors = targetProblemBehaviors.map(i => {
          return {
            id: i.id,
            code: i.code,
            code_definition: i.code_definition,
            color: i.color,
            is_show: true
          };
        });
      }

      // setup labels and hasPhaseLine value
      let newLabels = [];
      const phaseLineIndexes = [];
      labels.forEach((i, idx) => {
        if (!i) {
          newLabels[idx] = "";
          phaseLineIndexes.push(idx);
        } else {
          const display = isCumulative ? "month" : "date";
          let res = "";
          if (display === "date") {
            const arr = i.split("-").reverse();
            res = [`${arr.at(0)}/${arr.at(1)}`, arr.at(2)];
          }
          if (display === "month") {
            res = [moment(i).format("MMM"), moment(i).format("YYYY")];
          }
          newLabels[idx] = res;
        }
      });

      let newDatasets = [];
      if (datasets.length) {
        if (isCumulative && isStacked) {
          // setup core datasets property [stacked]
          datasets.forEach((i, idx) => {
            const bg =
              i.color +
              Math.round(Math.min(Math.max(0.75), 1) * 255)
                .toString(16)
                .toUpperCase();
            newDatasets[idx] = {
              label: i.label,
              data: i.data,
              backgroundColor: bg,
              borderColor: "#E4E4E4",
              hoverBorderColor: "#9A8AC2",
              borderWidth: 0,
              hoverBorderWidth: 2,
              borderRadius: 15
            };
          });
        } else {
          // setup core datasets property [line]
          if (isPrompting && progressBy !== "final_score") {
            // promting with frequency and percentage progress_by
            // prepare prompting property
            newDatasets = newPrompts.map(i => {
              return {
                promptId: i.id,
                label: i.name,
                data: [],
                // line style
                backgroundColor: PROMPT_COLORS[i.color].primaryColor,
                borderColor: PROMPT_COLORS[i.color].primaryColor,
                // point style
                pointStyle: PROMPT_POINTS[i.shape].pointStyle,
                pointBackgroundColor: PROMPT_COLORS[i.color].primaryColor,
                pointBorderColor: PROMPT_COLORS[i.color].secondaryColor,
                pointRadius: 4,
                hoverRadius: 4,
                fill: false,
                tension: 0.1
              };
            });
            // input prompting data value
            datasets[0].data.forEach(entry => {
              newPrompts.forEach((prompt, idx) => {
                let val = null;
                if (prompt.is_show && entry) {
                  val = entry[prompt.id];
                }
                newDatasets[idx].data.push(val);
              });
            });
            // add goal for prompting with frequency present
            if (
              progressBy === "frequency" &&
              target.prompting_format === "classic"
            ) {
              const goalDataset = {
                label: datasets[1].label,
                data: datasets[1].data,
                backgroundColor: "#71C115",
                borderColor: "#71C115",
                pointRadius: 0,
                fill: false,
                tension: 0.1
              };
              newDatasets.push(goalDataset);
            }
          } else {
            newDatasets = datasets.map(i => {
              const color = i.color ? i.color : "primary"; // set 'primary' for default color
              const propertyColor =
                i.label === "Goal" ? "#71C115" : PHASE_LINE_COLORS[color];
              return {
                label: i.label,
                data: i.data,
                backgroundColor: propertyColor,
                borderColor: propertyColor,
                pointRadius: i.label === "Goal" ? 0 : 4,
                hoverRadius: 4,
                fill: false,
                tension: 0.1
              };
            });
          }
        }
      }

      if (
        datasets &&
        datasets.data &&
        datasets.data.tasks &&
        datasets.data.problem_behaviors
      ) {
        newDatasets = [
          ...newTasks.map(i => ({
            taskId: i.id,
            type: "line",
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            pointBackgroundColor: i.color,
            pointBorderColor: i.color,
            pointRadius: 4,
            hoverRadius: 4,
            fill: false,
            tension: 0.1
          })),
          ...newProblemBehaviors.map(i => ({
            type: "bar",
            behaviorId: i.id,
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            yAxisID: "y-axis-bar",
            barPercentage: 10,
            barThickness: 100,
            maxBarThickness: 100,
            minBarLength: 10
          }))
        ];

        // Proses data untuk defaultTasks
        datasets.data.tasks.forEach(entry => {
          newTasks.forEach((task, idx) => {
            let val = null;
            if (task.is_show && entry && entry[task.id]) {
              val = entry[task.id].score;
            }
            newDatasets[idx].data.push(val);
          });
        });

        // Proses data untuk problem behaviors
        datasets.data.problem_behaviors.forEach(entry => {
          newProblemBehaviors.forEach((behavior, idx) => {
            let val = null;
            if (behavior.is_show && entry && entry[behavior.id]) {
              val = entry[behavior.id].score;
            }
            newDatasets[newTasks.length + idx].data.push(val);
          });
        });
      }

      // generate trendline and average line
      let isTargetHasTrendline = true;
      if (isCumulative) isTargetHasTrendline = false;
      if (isPrompting) {
        if (progressBy === "final_score") isTargetHasTrendline = true;
        else {
          const isShowPrompts = [...newPrompts].filter(i => i.is_show);
          if (isShowPrompts.length === 1) isTargetHasTrendline = true;
          else isTargetHasTrendline = false;
        }
      }
      if (isSbt) isTargetHasTrendline = false;

      let isTargetHasAverage = true;
      if (isCumulative) isTargetHasAverage = false;
      if (isPrompting || isSbt) isTargetHasAverage = false;
      if (phaseLineIndexes.length > 0) isTargetHasAverage = false;

      const trendlines = [];
      const averageLines = [];
      if (isTargetHasTrendline) {
        newDatasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            // trendline
            const arr = { a: [], b: [], c: [] };
            let firstAttemp = false;
            let endOfFirtsAttemp = false;
            dataset.data.forEach((val, index) => {
              /*
                    if val === null && index === 0  => first=true
                    if first
                      if      val === null && !endA => a.push
                      elseif  val !== null          => endA=true; b.push
                      elseif  val === null          => c.push
                    else
                      if      val !== null          => b.push
                      elseif  val === null          => c.push
                    end
                    */
              if (val === null && index === 0) {
                firstAttemp = true;
              }
              if (firstAttemp) {
                if (val === null && !endOfFirtsAttemp) {
                  arr.a.push(val);
                } else if (val !== null) {
                  endOfFirtsAttemp = true;
                  arr.b.push(val);
                } else if (val === null) arr.c.push(val);
              } else {
                if (val !== null) arr.b.push(val);
                else if (val === null) arr.c.push(val);
              }
            });
            // start generate trendline
            const idx = arr.b.length - 1;
            const dt = [
              arr.b[0],
              ...this.interpolate(arr.b[0], arr.b[idx], idx - 1),
              arr.b[idx]
            ];
            // remove the trendline if there are only 2 points
            if (dt.length === 2) {
              dt.pop();
            }
            let res = [...arr.a, ...dt, ...arr.c];
            const trendline = {
              key: "trendline",
              label: "Trendline",
              data: res,
              borderColor: "#6750A4",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 0
            };
            trendlines.push(trendline);
          }
        });
      }
      if (isTargetHasAverage) {
        newDatasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            // average line
            let totalScore = 0;
            let totalCount = 0;
            dataset.data.forEach(val => {
              if (val !== null) {
                totalScore += val;
                totalCount += 1;
              }
            });
            // start generate average
            const average = Number((totalScore / totalCount).toFixed(0));
            const resAverage = Array(totalCount).fill(average);
            const averageLine = {
              key: "average",
              label: "Average",
              data: resAverage,
              borderColor: "#71C115",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 2
            };
            averageLines.push(averageLine);
          }
        });
      }
      trendlines.forEach(i => {
        newDatasets.push(i);
      });
      averageLines.forEach(i => {
        newDatasets.push(i);
      });

      // generate legends -- empty

      const startDate = this.displayDate({
        date: this.report.start_date,
        format: "YYYY-MM-DD"
      });
      const endDate = this.displayDate({
        date: this.report.end_date,
        format: "YYYY-MM-DD"
      });
      const masteredDate = target.mastered_date;

      return {
        title: newTitle,
        targetId: target.id,
        goal: newGoal,
        hasPhaseLine: phaseLineIndexes.length > 0,
        prompts: newPrompts,
        tasks: newTasks,
        problemBehaviors: newProblemBehaviors,
        labels: newLabels,
        datasets: newDatasets,
        recordedBy: recordedBy,
        startDate: startDate,
        endDate: endDate,
        masteredDate: masteredDate,
        promptingProgressBy: progressBy
      };
    },
    interpolate(first, last, count) {
      const interpolatedPoints = [];

      // Calculate the difference between start and end coordinates
      const deltaX = (last - first) / (count + 1);

      // Interpolate the points
      for (let i = 1; i <= count; i++) {
        const interpolatedX = Number(first + deltaX * i);
        interpolatedPoints.push(interpolatedX);
      }

      return interpolatedPoints;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/reports/Editor.vue